'use client';
import { getInviteData, sendInviteEmail } from '@/api/account';
import { GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import { useInviteFriendStore } from '@/app/account/refer-a-friend/store/invite-friend-store';
import Button from '@/components/Button';
import Image from '@/components/Image';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import { ZL_USERINFO } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { CryptoUtils, SimpleShare } from '@/utils/tools';
import { Divider, Skeleton } from '@mui/material';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useInView } from 'react-intersection-observer';
import EmailInput from '../Form/EmailInput';
import { toast } from '../Toast';

const InviteAFriend = ({
  isFromAccountPage,
  bonus,
  inviteData,
  deviceType,
  btnTitle,
}: {
  isFromAccountPage?: boolean; // 是否是从个人中心页面邀请好友, 如果是, 则需要调用不同的埋点
  bonus: number;
  inviteData: GetResponseType<ApiResponseDefinition['GET:/api/customer/invite_register']>;
  deviceType: EPlatform;
  btnTitle?: string;
}) => {
  const t = useTranslations('inviteAFriend');
  const { refreshInvitationHistory, refreshClickShareCount } = useInviteFriendStore();
  const token = useStore((state) => state.token);
  const [firstClickTime, setFirstClickTime] = useState(0);
  const [clickCount, setClickCount] = useState(0);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [referLink, setReferLink] = useState('');
  const currencyList = useStore((state) => state.currencyList);
  const defaultCurrency = currencyList.find(
    (item) => item.name === env.NEXT_PUBLIC_DEFAULT_CURRENCY,
  );
  const [actionTypeBeforeLogin, setActionTypeBeforeLogin] = useState<
    'copyLink' | 'facebook' | 'twitter' | 'email' | 'messenger' | 'whatsApp'
  >();
  const [inviteBtnRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });
  const [shareToBtnRef, inViewShareToBtn] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);

  const { trackEvent } = useReporting();

  const form = useForm({
    defaultValues: {
      email: '',
    },
    mode: 'onChange',
  });
  // const { trackEvent } = useReporting();

  useEffect(() => {
    if (inView && !isFromAccountPage) {
      trackEvent(
        {
          eventName: 'InviteFriendPageInviteBtn_Exp',
          properties: {
            module_name: 'Start Saving',
          },
        },
        [ProviderType.SENSORS],
      );
    }
  }, [inView]);

  useEffect(() => {
    if (inViewShareToBtn && !isFromAccountPage) {
      trackEvent(
        {
          eventName: 'InviteFriendPageShareto_Exp',
          properties: {
            module_name: 'Start Saving',
          },
        },
        [ProviderType.SENSORS],
      );
    }
  }, [inViewShareToBtn]);

  const fetchInviteDataOnClient = async () => {
    const encryptEmail = getSharerEmail();
    const data = await getInviteData();
    if (data.code === 200) {
      if (data.data?.invite_params) {
        setReferLink(
          `${env.NEXT_PUBLIC_PARENT_IFRAME_URL}/recipient?${data.data?.invite_params}&sharer_email=${encryptEmail}`,
        );
      }
    }
  };

  useEffect(() => {
    const encryptEmail = getSharerEmail();
    if (inviteData?.invite_params) {
      setReferLink(
        `${env.NEXT_PUBLIC_PARENT_IFRAME_URL}/recipient?${inviteData?.invite_params}&sharer_email=${encryptEmail}`,
      );
    }
  }, [inviteData]);

  useEffect(() => {
    // 登录状态发生变化，其实就是关注从未登录到已登录
    if (token && !isFromAccountPage) {
      setReferLink('');
      // 获取新用户的邀请规则
      fetchInviteDataOnClient();
      // 刷新该用户的邀请历史数据
      refreshInvitationHistory(false, 1);
      if (actionTypeBeforeLogin === 'email') {
        handleInvite(`Share ${formatPriceNumberWithSymbol(`${bonus}`, defaultCurrency)}`);
      } else if (actionTypeBeforeLogin) {
        handleShare(actionTypeBeforeLogin);
      }
    }
  }, [token]);

  const getSharerEmail = () => {
    try {
      const cryptoUtils = new CryptoUtils();
      const userInfo = localStorage.getItem(ZL_USERINFO);
      const userObj = JSON.parse(userInfo || '{}') as any;
      const encryptedBase64UrlSafe = cryptoUtils.encrypt(userObj?.email);
      return encryptedBase64UrlSafe;
    } catch (error) {
      logger.log(error);
      return '';
    }
  };

  const handleShare = async (
    type: 'copyLink' | 'facebook' | 'twitter' | 'messenger' | 'whatsApp',
  ) => {
    const reportingProperties = isFromAccountPage
      ? {
          module_name: 'Invite Friend',
          btn_name: type === 'copyLink' ? 'copy link' : type,
        }
      : {
          module_name: 'Start Saving',
          share_channel_name: type === 'copyLink' ? 'copy link' : type,
        };
    trackEvent(
      {
        eventName: isFromAccountPage ? 'MinePageBtn_Click' : 'InviteFriendPageShareto_Click',
        properties: reportingProperties,
      },
      [ProviderType.SENSORS],
    );

    if (!token) {
      // 如果没有登录，点击这些按钮的功能，需要触发登录弹窗
      setShowLoginRegisterDialog(true);

      // 并记录登录前点击了什么操作
      setActionTypeBeforeLogin(type);
      return;
    }
    const params = {
      urlLink: `${referLink}&share_time=${Date.now()}`,
      title: t('emailTitle'),
      channelType: type,
    };
    const share = new SimpleShare({
      url: params.urlLink,
      title: params.title,
    });
    if (params.channelType === 'facebook') share.facebook();
    if (params.channelType === 'twitter') share.twitter();
    if (params.channelType === 'messenger') share.messenger();
    if (params.channelType === 'whatsApp') share.whatsApp();
    if (params.channelType === 'copyLink') {
      const valid = checkCopy();
      if (valid) {
        refreshClickShareCount();
        try {
          navigator.clipboard.writeText(`${referLink}&share_time=${Date.now()}`);
          toast.success(t('copySuccess'));
        } catch (err) {
          logger.error(err);
        }
      }
    }
    // 除去复制链接超出一分钟5次限制的情况，其余情况都需要加一
    if (params.channelType !== 'copyLink') {
      refreshClickShareCount();
    }
  };

  const checkCopy = () => {
    const currentTime = Date.now(); // 获取当前时间的时间戳
    // 如果是第一次点击或者距离上一次点击已经超过了一分钟
    if (firstClickTime === 0 || currentTime - firstClickTime >= 60000) {
      // 重置计数器和第一次点击时间
      setClickCount(1);
      setFirstClickTime(currentTime);
    } else {
      // 如果点击次数超过了5次
      if (clickCount + 1 > 5) {
        toast.warning(t('tooFrequent'));
        return false;
      }
      // 增加点击计数
      setClickCount((pre) => pre + 1);
    }
    return true;
  };

  const handleInvite = async (btn_name: string) => {
    trackEvent(
      {
        eventName: isFromAccountPage ? 'MinePageBtn_Click' : 'InviteFriendPageInviteBtn_Click',
        properties: isFromAccountPage
          ? {
              module_name: 'Invite Friend',
              btn_name,
            }
          : {
              module_name: 'Start Saving',
            },
      },
      [ProviderType.SENSORS],
    );

    if (!token) {
      // 如果没有登录，点击这些按钮的功能，需要触发登录弹窗
      setShowLoginRegisterDialog(true);
      // 并记录登录前点击了什么操作
      setActionTypeBeforeLogin('email');
      return;
    }
    const isValid = await form.trigger('email');
    if (!isValid) {
      return;
    }

    setSubmitLoading(true);
    try {
      const { code, msg } = await sendInviteEmail({
        email: form.getValues('email'),
      });
      if ([603, 601].includes(code)) {
        // 邮箱已注册
        form.setError('email', { message: msg });
      } else if (code === 602) {
        toast.error(msg);
      } else if (code === 200) {
        toast.success(msg);
        form.resetField('email');

        // 成功邀请朋友后，刷新邀请历史获取最新数据
        refreshInvitationHistory(false, 1);
        refreshClickShareCount();
      }
    } catch (error) {
      logger.log(error);
    } finally {
      setSubmitLoading(false);
    }
  };
  // 下面的mb-46px是因为有错误提示占用两行的空间
  // TODO：其实更好的做法应该是toast提示？
  return (
    <>
      <div className="w-full px-[24px] mt-[28px] lg:px-0">
        <FormProvider {...form}>
          <EmailInput
            formItemClassName="relative"
            id="outlined-basic"
            className="w-[323px] lg:w-full"
            placeholder={t('emailPlaceholder')}
            name="email"
            label=""
            control={form.control}
            useNativeError
            actionBtnProps={
              deviceType !== EPlatform.WEB
                ? {
                    onClick: () => handleInvite(t('shareBonusAmount', { amount: bonus })),
                    loading: submitLoading,
                    name:
                      btnTitle ||
                      t('shareBonusAmount', {
                        amount: formatPriceNumberWithSymbol(`${bonus}`, defaultCurrency, false, 0),
                      }),
                    sdExpProps: {
                      sdEventName: 'InviteFriendPageInviteBtn_Exp',
                      sdEventParams: {
                        module_name: 'Start Saving',
                      },
                    },
                  }
                : undefined
            }
          />
        </FormProvider>
      </div>
      {deviceType === EPlatform.WEB ? (
        <div className="w-full mt-26">
          {bonus ? (
            <Button
              variant="contained"
              className="w-full h-48 font-br"
              radiusSize={'small'}
              onClick={() =>
                handleInvite(`Share ${formatPriceNumberWithSymbol(`${bonus}`, defaultCurrency)}`)
              }
              loading={submitLoading}
              ref={inviteBtnRef}
            >
              {btnTitle ||
                t('shareBonusAmount', {
                  amount: formatPriceNumberWithSymbol(`${bonus}`, defaultCurrency, false, 0),
                })}
            </Button>
          ) : (
            <Skeleton variant="rectangular" className="w-full" height={36} />
          )}
        </div>
      ) : null}
      <div className="w-[302px] mt-[6.4vw] lg:mt-32 mx-auto">
        <Divider>
          <span className="text-primary-text font-br text-12">{t('OR')}</span>
        </Divider>
      </div>
      <div
        ref={shareToBtnRef}
        className="w-full mt-[5.333vw] mb-[5.333vw] lg:mb-0 lg:mt-16 flex justify-around items-center"
      >
        <div
          className="flex flex-col items-center justify-center  basis-1/3  cursor-pointer copy-btn"
          onClick={() => handleShare('copyLink')}
        >
          <Image src="/referAFriend/copyLink.png" width={40} height={40} alt={t('copyLink')} />
          <span className="text-12 mt-8  font-br whitespace-nowrap">{t('copyLink')}</span>
        </div>

        <div
          className="flex flex-col items-center justify-center  basis-1/3  cursor-pointer"
          onClick={() => handleShare('facebook')}
        >
          <Image src="/referAFriend/facebookLogo.png" width={40} height={40} alt={t('facebook')} />
          <span className="text-12 mt-8  font-br">{t('facebook')}</span>
        </div>

        <div
          className="flex flex-col items-center justify-center basis-1/3  cursor-pointer"
          onClick={() => handleShare('twitter')}
        >
          <Image src="/referAFriend/twitterLogo.png" width={40} height={40} alt={t('twitter')} />
          <span className="text-12 mt-8  font-br">{t('twitter')}</span>
        </div>

        <div
          className="flex flex-col items-center justify-center basis-1/3  cursor-pointer"
          onClick={() => handleShare('messenger')}
        >
          <Image src="/referAFriend/Messenger.png" width={40} height={40} alt={t('Messenger')} />
          <span className="text-12 mt-8  font-br">{t('Messenger')}</span>
        </div>

        <div
          className="flex flex-col items-center justify-center basis-1/3  cursor-pointer"
          onClick={() => handleShare('whatsApp')}
        >
          <Image src="/referAFriend/WhatsApp.png" width={40} height={40} alt={t('WhatsApp')} />
          <span className="text-12 mt-8  font-br">{t('WhatsApp')}</span>
        </div>
      </div>
    </>
  );
};

export default InviteAFriend;
